import React from "react";

import {
  childrensBookIllustrationsByUnit,
  currentChildrensBookCover,
} from "../utils/childrensBookAssetState";

import {
  plainTextFromManuscriptEntry,
} from "../utils/childrensBookManuscriptContent";

import {
  buildChildrensBookUnitDiagnostics,
  summarizeChildrensBookDiagnostics,
} from "../utils/childrensBookUnitDiagnostics";

import {
  buildPublicationHandoffSummary,
} from "./ChildrensBookPublicationHandoff";

function textPresent(value) {
  return Boolean(
    String(value ?? "").trim()
  );
}

function unitRequiresText(unit) {
  return (
    String(
      unit?.textAmount || ""
    )
      .trim()
      .toLowerCase() !== "none"
  );
}

function decisionMade(value) {
  const normalized =
    String(value ?? "").trim();

  return (
    textPresent(normalized) &&
    normalized !== "not_decided"
  );
}

function trimSizeComplete(
  trimSize,
  customTrimSize
) {
  const normalized =
    String(trimSize ?? "").trim();

  if (!decisionMade(normalized)) {
    return false;
  }

  if (
    normalized ===
    "custom_dimensions"
  ) {
    return textPresent(
      customTrimSize
    );
  }

  return true;
}

export function getWorkspaceStatuses(
  projectPlan = {},
  childrensBookAssets = []
) {
  const bookUnits =
    Array.isArray(
      projectPlan.bookMap?.units
    )
      ? projectPlan.bookMap.units
      : [];

  const projectChecks = [
    textPresent(
      projectPlan.identity?.title
    ),
    textPresent(
      projectPlan.audience?.ageRange
    ),
    decisionMade(
      projectPlan.audience
        ?.readingLevel
    ),
    textPresent(
      projectPlan.bookForm?.type
    ),
    trimSizeComplete(
      projectPlan.visualFormat
        ?.trimSize,
      projectPlan.visualFormat
        ?.customTrimSize
    ),
    textPresent(
      projectPlan.collaboration?.mode
    ),
  ];

  const projectCompleted =
    projectChecks.filter(Boolean).length;

  const projectPlanStatus =
    projectCompleted === 0
      ? "not_started"
      : projectCompleted ===
          projectChecks.length
        ? "ready"
        : "in_progress";

  const bookMapStatus =
    bookUnits.length === 0
      ? "not_started"
      : "ready";

  const manuscriptEntries =
    Array.isArray(
      projectPlan.manuscriptPlan
        ?.entries
    )
      ? projectPlan.manuscriptPlan
          .entries
      : [];

  const completedManuscriptIds =
    new Set(
      manuscriptEntries
        .filter(
          (entry) =>
            textPresent(
              plainTextFromManuscriptEntry(
                entry
              )
            ) &&
            entry.status ===
              "complete"
        )
        .map(
          (entry) => entry.unitId
        )
    );

  const manuscriptStatus =
    bookUnits.length === 0
      ? "not_started"
      : bookUnits.every(
          (unit) =>
            !unitRequiresText(unit) ||
            completedManuscriptIds.has(
              unit.id
            )
        )
        ? "ready"
        : manuscriptEntries.length > 0
          ? "in_progress"
          : "not_started";

  const requiredIllustrationUnits =
    bookUnits.filter(
      (unit) =>
        unit.illustrationPlan &&
        unit.illustrationPlan !==
          "none"
    );

  const illustrationEntries =
    Array.isArray(
      projectPlan.illustrationPlan
        ?.entries
    )
      ? projectPlan.illustrationPlan
          .entries
      : [];

  const completedIllustrationIds =
    new Set(
      illustrationEntries
        .filter(
          (entry) =>
            entry.status ===
              "complete"
        )
        .map(
          (entry) =>
            entry.unitId
        )
    );

  const illustrationStatus =
    bookUnits.length === 0
      ? "not_started"
      : requiredIllustrationUnits
          .length === 0 ||
          requiredIllustrationUnits
            .every(
              (unit) =>
                completedIllustrationIds
                  .has(unit.id)
            )
        ? "ready"
        : illustrationEntries.length > 0
          ? "in_progress"
          : "not_started";

  const coverRequired = [
    projectPlan.coverPlan?.title,
    projectPlan.coverPlan
      ?.authorName,
  ];

  const coverCompleted =
    coverRequired.filter(
      textPresent
    ).length;

  const coverStatus =
    coverCompleted === 0
      ? "not_started"
      : coverCompleted ===
          coverRequired.length
        ? "ready"
        : "in_progress";

  const activeCover =
    currentChildrensBookCover(
      childrensBookAssets,
      "front_cover"
    );

  const illustrationsByUnit =
    childrensBookIllustrationsByUnit(
      childrensBookAssets
    );

  const manuscriptTextIds =
    new Set(
      manuscriptEntries
        .filter(
          (entry) =>
            textPresent(
              plainTextFromManuscriptEntry(
                entry
              )
            )
        )
        .map(
          (entry) => entry.unitId
        )
    );

  const bookAssemblyStatus =
    bookUnits.length === 0
      ? "not_started"
      : (
          Boolean(activeCover) &&
          bookUnits.every(
            (unit) =>
              (
                !unitRequiresText(
                  unit
                ) ||
                manuscriptTextIds.has(
                  unit.id
                )
              ) &&
              (
                !unit.illustrationPlan ||
                unit.illustrationPlan ===
                  "none" ||
                Boolean(
                  illustrationsByUnit[
                    unit.id
                  ]
                )
              )
          )
        )
        ? "ready"
        : "needs_attention";

  const readinessStatus = [
    projectPlanStatus,
    bookMapStatus,
    manuscriptStatus,
    illustrationStatus,
    coverStatus,
    bookAssemblyStatus,
  ].every(
    (status) => status === "ready"
  )
    ? "ready"
    : "in_progress";

  const publicationHandoffSummary =
    buildPublicationHandoffSummary(
      projectPlan,
      childrensBookAssets
    );

  const publicationHandoffStarted =
    Boolean(
      publicationHandoffSummary.title ||
      publicationHandoffSummary.author ||
      publicationHandoffSummary.units
        .length ||
      publicationHandoffSummary.activeCover
    );

  const publicationHandoffStatus =
    publicationHandoffSummary.ready
      ? "ready"
      : publicationHandoffStarted
        ? "in_progress"
        : "not_started";

  const publishedBookStatus =
    projectPlan
      .publicationReadiness
      ?.status ===
        "published_nextscenes"
      ? "published"
      : "not_published";

  return {
    projectPlan: projectPlanStatus,
    bookMap: bookMapStatus,
    manuscript: manuscriptStatus,
    illustrations:
      illustrationStatus,
    cover: coverStatus,
    bookAssembly:
      bookAssemblyStatus,
    publicationReadiness:
      readinessStatus,
    publicationHandoff:
      publicationHandoffStatus,
    publishedBook:
      publishedBookStatus,
  };
}

function statusStyle(
  status,
  active
) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "3px 7px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  };

  if (active) {
    return {
      ...base,
      background:
        "rgba(255,255,255,0.22)",
      color: "#ffffff",
    };
  }

  if (
    status === "ready" ||
    status === "published"
  ) {
    return {
      ...base,
      background: "#dcfce7",
      color: "#166534",
    };
  }

  if (
    status ===
    "needs_attention"
  ) {
    return {
      ...base,
      background: "#ffedd5",
      color: "#9a3412",
    };
  }

  if (status === "in_progress") {
    return {
      ...base,
      background: "#fef3c7",
      color: "#92400e",
    };
  }

  return {
    ...base,
    background: "#e2e8f0",
    color: "#475569",
  };
}

function BookBlueprintMark() {
  return (
    <svg
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M12 14.5c7.7-1.2 14.2.4 20 4.9v32.1c-5.8-4.5-12.3-6.1-20-4.9V14.5Z"
        fill="currentColor"
        opacity="0.18"
      />
      <path
        d="M52 14.5c-7.7-1.2-14.2.4-20 4.9v32.1c5.8-4.5 12.3-6.1 20-4.9V14.5Z"
        fill="currentColor"
        opacity="0.30"
      />
      <path
        d="M12 14.5c7.7-1.2 14.2.4 20 4.9 5.8-4.5 12.3-6.1 20-4.9v32.1c-7.7-1.2-14.2.4-20 4.9-5.8-4.5-12.3-6.1-20-4.9V14.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path
        d="M32 19.4v32.1"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M20 25.5h6.5M20 32h6.5M37.5 25.5H44M37.5 32H44"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function ChildrensBookWorkspaceNavigator({
  lang = "en",
  currentWorkspace =
    "project-plan",
  projectTitle = "",
  projectPlan = {},
  childrensBookAssets = [],
  onNavigate,
}) {
  const isFR =
    String(lang).toLowerCase() ===
    "fr";

  const statuses =
    getWorkspaceStatuses(
      projectPlan,
      childrensBookAssets
    );

  const unitDiagnostics =
    buildChildrensBookUnitDiagnostics(
      projectPlan,
      childrensBookAssets,
      {
        lang: isFR
          ? "fr"
          : "en",
      }
    );
    const diagnosticLang =
      isFR
        ? "fr"
        : "en";

    const manuscriptDiagnosticText =
      summarizeChildrensBookDiagnostics(
        unitDiagnostics.manuscript,
        {
          lang:
            diagnosticLang,
          context:
            "manuscript",
        }
      );

    const illustrationDiagnosticText =
      summarizeChildrensBookDiagnostics(
        unitDiagnostics
          .illustrationPlan,
        {
          lang:
            diagnosticLang,
          context:
            "illustration",
        }
      );

    const assemblyDiagnosticText =
      summarizeChildrensBookDiagnostics(
        [
          ...unitDiagnostics.cover,
          ...unitDiagnostics.assembly,
        ],
        {
          lang:
            diagnosticLang,
          context:
            "assembly",
        }
      );

    const publicationDiagnosticText =
      summarizeChildrensBookDiagnostics(
        [
          ...unitDiagnostics.manuscript,
          ...unitDiagnostics
            .illustrationPlan,
          ...unitDiagnostics.cover,
          ...unitDiagnostics.assembly.filter(
            (item) =>
              item.kind ===
              "missing_illustration_asset"
          ),
        ],
        {
          lang:
            diagnosticLang,
          context:
            "publication",
        }
      );

    const statusText = {
    not_started: isFR
      ? "Non commencé"
      : "Not started",
    in_progress: isFR
      ? "En cours"
      : "In progress",
    needs_attention: isFR
      ? "À compléter"
      : "Needs attention",
    ready: isFR
      ? "Prêt"
      : "Ready",
    published: isFR
      ? "Publié"
      : "Published",
    not_published: isFR
      ? "Non publié"
      : "Not published",
  };

  const progressStatuses = [
    statuses.projectPlan,
    statuses.bookMap,
    statuses.manuscript,
    statuses.illustrations,
    statuses.cover,
    statuses.bookAssembly,
    statuses.publicationReadiness,
  ];

  const readyCount =
    progressStatuses.filter(
      (status) =>
        status === "ready"
    ).length;

  const statusTotal =
    progressStatuses.length;

  const publishedOnNextScenes =
    statuses.publishedBook ===
    "published";

  const workspaces = [
    {
      id: "blueprint",
      label: isFR
        ? "Vue d’ensemble"
        : "Overview",
      status:
        readyCount === statusTotal
          ? "ready"
          : "in_progress",
      statusLabel: isFR
        ? `${readyCount} sur ${statusTotal} prêts`
        : `${readyCount} of ${statusTotal} ready`,
      diagnostic:
        publicationDiagnosticText,
    },
    {
      id: "project-plan",
      label: isFR
        ? "Plan du projet"
        : "Project Plan",
      status:
        statuses.projectPlan,
    },
    {
      id: "book-map",
      label: isFR
        ? "Plan du livre"
        : "Book Structure",
      status: statuses.bookMap,
    },
    {
      id: "manuscript-plan",
      label: isFR
        ? "Manuscrit"
        : "Manuscript",
      status:
        statuses.manuscript,
      diagnostic:
        manuscriptDiagnosticText,
    },
    {
      id: "illustration-plan",
      label: isFR
        ? "Illustrations"
        : "Illustrations",
      status:
        statuses.illustrations,
      diagnostic:
        illustrationDiagnosticText,
    },
    {
      id: "cover-plan",
      label: isFR
        ? "Couverture"
        : "Cover",
      status: statuses.cover,
    },
    {
      id: "book-assembly",
      label: isFR
        ? "Réviser votre livre"
        : "Review Your Book",
      status:
        statuses.bookAssembly,
      diagnostic:
        assemblyDiagnosticText,
    },
    {
      id:
        "publication-readiness",
      label: isFR
        ? "Préparer pour publication"
        : "Prepare for Publication",
      status:
        statuses
          .publicationReadiness,
      diagnostic:
        publicationDiagnosticText,
    },

    ...(
      publishedOnNextScenes ||
      currentWorkspace ===
        "published-book"
        ? [
            {
              id:
                "published-book",
              label: isFR
                ? "Livre publié"
                : "Published Book",
              status:
                statuses
                  .publishedBook,
            },
          ]
        : []
    ),
  ];

  const normalizedCurrent =
    currentWorkspace ===
      "project-plan-review"
      ? "project-plan"
      : currentWorkspace ===
          "book-preview"
        ? "book-assembly"
        : (
            currentWorkspace ===
              "publication-proof" ||
            currentWorkspace ===
              "publication-handoff"
          )
          ? "publication-readiness"
          : currentWorkspace;

  const displayTitle =
    textPresent(projectTitle)
      ? projectTitle
      : isFR
        ? "Livre pour enfants sans titre"
        : "Untitled Children’s Book";

  return (
    <nav
      aria-label={
        isFR
          ? "Navigation du NextScenes Book Blueprint"
          : "NextScenes Book Blueprint navigation"
      }
      className="ns-card ns-nbb-section-shell ns-nbb-navigator"
      style={{
        padding: 16,
        marginBottom: 20,
        borderRadius: 18,
        border:
          "1px solid rgba(234,88,12,0.24)",
        background:
          "linear-gradient(180deg, #fffaf5 0%, #ffffff 100%)",
      }}
    >
      <div className="ns-nbb-identity-row">
        <div className="ns-nbb-identity-lockup">
          <div
            className="ns-nbb-identity-mark"
            aria-hidden="true"
          >
            <BookBlueprintMark />
          </div>

          <div className="ns-nbb-identity-copy">
            <div className="ns-nbb-eyebrow">
              NextScenes Book Blueprint
            </div>

            <div className="ns-nbb-navigator-title">
              {displayTitle}
            </div>
          </div>
        </div>

        <div className="ns-nbb-status-block">
          <span className="ns-nbb-status-badge">
            {isFR
              ? `${readyCount} sur ${statusTotal} prêts`
              : `${readyCount} of ${statusTotal} ready`}
          </span>

          <span className="ns-nbb-navigator-guidance">
            {isFR
              ? "Choisissez où travailler"
              : "Choose where to work"}
          </span>
        </div>
      </div>

      <ul
        className="ns-nbb-workspace-grid"
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(155px, 1fr))",
          gap: 8,
          padding: 0,
          margin: 0,
          listStyle: "none",
        }}
      >
        {workspaces.map(
          (workspace) => {
            const active =
              normalizedCurrent ===
              workspace.id;

            const label =
              workspace.statusLabel ||
              statusText[
                workspace.status
              ];

            return (
              <li key={workspace.id}>
                <button
                  className={
                    active
                      ? "ns-nbb-workspace-button ns-nbb-workspace-button-active"
                      : "ns-nbb-workspace-button"
                  }
                  type="button"
                  aria-current={
                    active
                      ? "page"
                      : undefined
                  }
                  onClick={() => {
                    if (
                      typeof onNavigate ===
                      "function"
                    ) {
                      onNavigate(
                        workspace.id
                      );
                    }
                  }}
                  style={{
                    width: "100%",
                    minHeight: 64,
                    padding: "10px 11px",
                    borderRadius: 12,
                    border: active
                      ? "1px solid #c2410c"
                      : "1px solid #dbe2ea",
                    background: active
                      ? "#c2410c"
                      : "#ffffff",
                    color: active
                      ? "#ffffff"
                      : "#1e293b",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection:
                      "column",
                    alignItems:
                      "flex-start",
                    justifyContent:
                      "space-between",
                    gap: 7,
                    textAlign: "left",
                    font: "inherit",
                  }}
                >
                  <span
                    className="ns-nbb-workspace-label"
                    style={{
                      fontWeight: 900,
                      lineHeight: 1.25,
                    }}
                  >
                    {workspace.label}
                  </span>

                  <span
                    style={statusStyle(
                      workspace.status,
                      active
                    )}
                  >
                    {label}
                  </span>

                  {workspace.diagnostic &&
                  (
                    workspace.status ===
                      "in_progress" ||
                    workspace.status ===
                      "needs_attention"
                  ) ? (
                    <span
                      className="ns-nbb-workspace-diagnostic"
                      style={{
                        fontSize: 11,
                        lineHeight: 1.4,
                        fontWeight: 600,
                        color: active
                          ? "rgba(255,255,255,0.94)"
                          : "#7c2d12",
                      }}
                    >
                      {
                        workspace.diagnostic
                      }
                    </span>
                  ) : null}
                </button>
              </li>
            );
          }
        )}
      </ul>
    </nav>
  );
}
